import React, { useCallback, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Sprite, SparkleBurst, CharacterView, GlowRing, PromptCapsule } from "./fx";

// Carry — teamwork: the bags are HEAVY, so they lag as you carry them. Drag
// each to the bench for a tired neighbor. Faithful port of iOS
// CarryActivityView (a heavy, springy lag, not a flick).

const BENCH_UNIT = { x: 0.72, y: 0.42 };

const Carry = ({ payload, sounds, onDone }) => {
  const totalBags = payload.bags || 2;
  const doneRef = useRef(false);
  const stageRef = useRef(null);
  const benchRef = useRef(null);

  const [placed, setPlaced] = useState(() => new Set());
  const [dragging, setDragging] = useState(null); // {i, x, y}
  const [allDone, setAllDone] = useState(false);
  const [burst, setBurst] = useState(0);

  const bagHome = (i) => ({ left: `${(0.18 + i * 0.16) * 100}%`, top: "78%" });

  const atBench = useCallback((clientX, clientY) => {
    const r = benchRef.current?.getBoundingClientRect();
    if (!r) return false;
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    return Math.hypot(clientX - cx, clientY - cy) < 140;
  }, []);

  const handleDown = useCallback((e, i) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const rect = stageRef.current?.getBoundingClientRect();
    setDragging({ i, x: e.clientX - (rect?.left || 0), y: e.clientY - (rect?.top || 0) });
  }, []);

  const handleMove = useCallback((e) => {
    setDragging((d) => {
      if (!d) return d;
      const rect = stageRef.current?.getBoundingClientRect();
      return { ...d, x: e.clientX - (rect?.left || 0), y: e.clientY - (rect?.top || 0) };
    });
  }, []);

  const handleUp = useCallback(
    (e, i) => {
      if (atBench(e.clientX, e.clientY)) {
        const next = new Set(placed).add(i);
        setPlaced(next);
        setDragging(null);
        sounds.playPop(0.5 + placed.size * 0.2);
        sounds.playSparkle();
        setBurst((b) => b + 1);
        if (next.size >= totalBags && !doneRef.current) {
          doneRef.current = true;
          setAllDone(true);
          sounds.playPop(0.95);
          window.setTimeout(() => onDone(), 1100);
        }
      } else {
        setDragging(null); // slips back home (heavy!)
      }
    },
    [atBench, onDone, placed, sounds, totalBags]
  );

  return (
    <div className="ch-activity-body ch-carry">
      <PromptCapsule text={payload.prompt} sounds={sounds} />

      <div className="ch-stage" ref={stageRef} style={{ touchAction: "none" }}>
        <div className="ch-carry-neighbor" style={{ left: "90%", top: `${BENCH_UNIT.y * 100}%` }}>
          <motion.div
            key={placed.size}
            style={{ width: "100%", height: "100%" }}
            initial={{ y: 0 }}
            animate={placed.size > 0 ? { y: [0, -14, 0] } : { y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <CharacterView character={payload.sideCharacter} happy={allDone} />
          </motion.div>
        </div>

        <div className="ch-carry-bench" style={{ left: `${BENCH_UNIT.x * 100}%`, top: `${BENCH_UNIT.y * 100}%` }}>
          <GlowRing hot={!!dragging} tint="#ff7a5c" style={{ bottom: "-28px" }} />
          <span ref={benchRef} className="ch-carry-bench-img">
            <Sprite name={payload.benchAsset || "ch_bench"} />
          </span>
          <span className="ch-carry-stacked">
            {Array.from(placed).map((i) => (
              <motion.span
                key={i}
                className="ch-carry-stacked-bag"
                initial={{ scale: 0, y: -18 }}
                animate={{ scale: 1, y: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 14 }}
              >
                <Sprite name={payload.bagAsset || "ch_bag"} />
              </motion.span>
            ))}
          </span>
          <SparkleBurst trigger={burst} reach={90} style={{ left: "50%", top: "40%" }} />
        </div>

        {/* Persistent, interactive bags. The one being carried goes invisible
            (its pointer capture must stay mounted) while a springy follower
            lags heavily toward the finger. */}
        {Array.from({ length: totalBags }, (_, i) => {
          if (placed.has(i)) return null;
          const lifted = dragging?.i === i;
          return (
            <div
              key={i}
              className="ch-carry-bag"
              style={{ ...bagHome(i), opacity: lifted ? 0 : 1 }}
              onPointerDown={(e) => handleDown(e, i)}
              onPointerMove={handleMove}
              onPointerUp={(e) => handleUp(e, i)}
              onPointerCancel={(e) => handleUp(e, i)}
              role="button"
              tabIndex={-1}
              aria-label="Heavy bag"
            >
              <Sprite name={payload.bagAsset || "ch_bag"} />
            </div>
          );
        })}

        {dragging && (
          <motion.div
            className="ch-carry-bag is-lifted"
            animate={{ left: dragging.x, top: dragging.y }}
            transition={{ type: "spring", stiffness: 90, damping: 16 }}
          >
            <Sprite name={payload.bagAsset || "ch_bag"} />
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Carry;
